import React, { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
    [
        "inline-flex items-center justify-center rounded uppercase tracking-widest text-[11px] font-bold font-mono",
        "transition-all duration-200 focus-visible:outline-none focus-visible:ring-1",
        "disabled:pointer-events-none disabled:opacity-30",
        "active:scale-[0.97] transform"
    ],
    {
        variants: {
            variant: {
                primary: [
                    "bg-[#4da760] text-white border border-[#4da760]",
                    "hover:bg-[#9333EA] hover:shadow-[0_0_15px_rgba(72,153,83,100)]",
                ],
                secondary: [
                    "bg-[#12151C] text-slate-300 border border-[#1A1D24]",
                    "hover:text-white hover:border-slate-500 hover:bg-[#161A23]"
                ],
                outline: [
                    "border border-[#4da760] bg-transparent text-[#4da760]",
                    "hover:bg-[#4da760]/10 hover:text-white"
                ],
                ghost: [
                    "text-slate-400 hover:text-white hover:bg-[#12151C]"
                ],
                danger: [
                    "bg-[#FF3333]/10 text-[#FF3333] border border-[#FF3333]/30",
                    "hover:bg-[#FF3333] hover:text-white hover:border-[#FF3333]"
                ],
                success: [
                    "bg-[#50A970]/10 text-[#50A970] border border-[#50A970]/30",
                    "hover:bg-[#50A970] hover:text-white hover:border-[#50A970]"
                ],
                link: [
                    "text-[#4da760] underline-offset-4 hover:underline"
                ]
            },
            size: {
                sm: "h-8 px-3 py-1",
                default: "h-10 px-5 py-2",
                lg: "h-12 px-6 py-3",
                xl: "h-14 px-8 py-4",
                icon: "h-9 w-9"
            },
            fullWidth: {
                true: "w-full"
            }
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
            fullWidth: false
        }
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    loading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, fullWidth, loading = false, disabled, leftIcon, rightIcon, children, ...props }, ref) => {
        const isDisabled = disabled || loading;

        return (
            <button
                className={cn(buttonVariants({ variant, size, fullWidth }), className)}
                ref={ref}
                disabled={isDisabled}
                {...props}
            >
                {loading && (
                    <Loader2 className={cn("animate-spin h-3.5 w-3.5", children && "mr-2")} />
                )}

                {!loading && leftIcon && (
                    <span className={cn(size === 'sm' ? "mr-1" : "mr-2")}>
                        {leftIcon}
                    </span>
                )}

                {children}

                {!loading && rightIcon && (
                    <span className={cn(size === 'sm' ? "ml-1" : "ml-2")}>
                        {rightIcon}
                    </span>
                )}
            </button>
        );
    }
);

Button.displayName = 'Button';
export { buttonVariants };